import { Button } from "@winche/ui";
import { ConfirmDialog } from "./ui";
import { isFreeModelId } from "../lib/or-models";
import {
  changeNeedsFreeTierAck,
  freeTierRetentionWarning,
  modelChangeScope,
  modelChangeSummary,
  modelChangeTitle,
  revertOf,
  type ModelChange,
} from "../lib/model-change";

/**
 * Confirmation of a model change (ux-admin-02): the scope («Every AI call for
 * every user moves from X to Y…») and, for a free-tier model, the retention
 * warning with FREE to type. `onConfirm` gets whether the operator
 * acknowledged the free tier, for the callable.
 */
export function ModelChangeDialog({
  change,
  onCancel,
  onConfirm,
}: {
  change: ModelChange | null;
  onCancel: () => void;
  onConfirm: (change: ModelChange, acknowledgeFreeTierRetention: boolean) => Promise<void>;
}) {
  const warning = change ? freeTierRetentionWarning(change, isFreeModelId) : null;
  const needsAck = change ? changeNeedsFreeTierAck(change, isFreeModelId) : false;
  return (
    <ConfirmDialog
      open={change !== null}
      onOpenChange={(open) => !open && onCancel()}
      title={change ? modelChangeTitle(change) : ""}
      scope={
        change ? (
          <>
            <span className="block">{modelChangeScope(change)}</span>
            {warning ? <span className="mt-2 block font-medium text-warning-fg">{warning}</span> : null}
          </>
        ) : null
      }
      confirmLabel={
        change?.type === "freeTier"
          ? change.to
            ? "Use free tier"
            : "Stop free tier"
          : change?.type === "fallback" && !change.to
            ? "Remove fallback"
            : "Switch model"
      }
      typeToConfirm={needsAck ? "FREE" : undefined}
      onConfirm={async () => {
        if (change) await onConfirm(change, needsAck);
      }}
    />
  );
}

/** «Text model: X → Y. Revert»: the last change made here, and the way back. */
export function ModelChangeNotice({ change, onRevert, disabled }: { change: ModelChange; onRevert: (revert: ModelChange) => void; disabled?: boolean }) {
  const revert = revertOf(change);
  return (
    <p className="mt-4 flex flex-wrap items-center gap-2 rounded-md bg-sunken px-3 py-2 text-caption text-muted-foreground" aria-live="polite">
      <span>Changed. {modelChangeSummary(change)}</span>
      {revert ? (
        <Button variant="outline" size="xs" onClick={() => onRevert(revert)} disabled={disabled}>
          Revert
        </Button>
      ) : null}
    </p>
  );
}
